<template>
  <!--
    The tour's one mount point (spec 25 section 5.2), beside <terms-gate> in
    app.vue. It renders NOTHING when no tour is running, which is the state of
    every page for every real tenant: a tour only starts on the public demo
    (GET /organization's `demo` object, the same server-side fact that
    disables Send and Upload there).

    Two kinds of tour run here. A STORY (spec 25: the sales demo) points at
    things. A TASK walkthrough (spec 33) is training (Mark, 2026-10-12): each
    step tells the visitor what to do in their own demo tenant, rings the one
    control to use, puts what to type in front of them, and moves on when the
    page or the server shows they have done it.

    Spec 53 adds a third place: a PRACTICE FLEET, a demo lease that carries a
    training enrollment. There only definitions written for training start
    (`audience`, mayStart), the card counts "Step 2 of 4" under a course bar,
    and every step's outcome goes in a step log the grader reads at the end
    (training/). A real tenant still never starts anything.
  -->
  <div v-if="active" class="lw-p-tour">
    <!-- Keyed by the control it rings, so each new control gets the pulse
         afresh: a few beats to draw the eye, then a steady outline. -->
    <div
      v-if="running && ringRect && !filming"
      :key="ringKey"
      class="lw-p-tour__ring"
      :class="{
        'lw-p-tour__ring--still': reducedMotion,
        'lw-p-tour__ring--done': phase === 'done',
      }"
      :style="ringStyle"
      data-test="tour-ring"
      aria-hidden="true"
    />
    <!-- What to do with the ringed control, right beside it ("Type Cascadia",
         "Choose Van"). The card says the same in a sentence for a screen
         reader; this one is for the eye, which is on the ring. -->
    <p
      v-if="running && ringRect && tagText && !filming"
      :key="`tag-${ringKey}`"
      class="lw-p-tour__tag"
      :class="{ 'lw-p-tour__tag--below': tagBelow }"
      :style="tagStyle"
      data-test="tour-tag"
      aria-hidden="true"
    >
      {{ tagText }}
    </p>

    <!-- Filming a training video (training/render.js), none of this is drawn:
         no ring, no tag, no card, no hint in an empty box, no scrim. The video
         is the bare app, and Relay, laid over it afterwards, points at what
         the ring would have (Mark, 2026-10-27). The ring is still measured
         and reported for him (reportStage). -->
    <LwTourCard
      v-if="running && shown && !filming"
      ref="card"
      :chapter-title="shown.chapterTitle"
      :title="shown.title"
      :body="shown.body"
      :step-number="kind === 'task' ? groupNumber : view.index + 1"
      :total="kind === 'task' ? groupTotal : view.total"
      :count-text="inTraining ? `Step ${groupNumber} of ${groupTotal}` : ''"
      :course="inTraining ? course : null"
      :why="shown.why || ''"
      :close-label="inTraining ? 'Finish' : 'Close'"
      :is-first="view.isFirst"
      :is-last="view.isLast"
      :actions="cardActions"
      :footnote="shown.cta ? priceLine : ''"
      :docked="docked"
      :centered="!docked && !targetEl && !ringEl"
      :corner="kind === 'task' && !docked && Boolean(targetEl || ringEl)"
      :corner-side="cardCorner"
      :corner-top="cardTop"
      :position="position"
      :ack="ackLine"
      :instruction="instruction"
      :status="statusLine"
      :controls="controls"
      :show-next="!isAction && !hasChoices && !driveHolds"
      :can-back="view.canBack !== false"
      :skippable="isAction"
      :noun="kind === 'task' ? 'walkthrough' : 'tour'"
      @next="next"
      @back="back"
      @exit="exit"
      @action="onAction"
      @control="onControl"
      @skip="skip"
    />

    <button
      v-if="paused"
      type="button"
      class="lw-p-tour__pill"
      data-test="tour-resume"
      @click="resume"
    >
      {{ kind === 'task' ? 'Resume the walkthrough' : 'Resume the tour' }}
    </button>

    <p class="lw-p-tour__live" aria-live="polite">{{ announcement }}</p>
  </div>
</template>

<script>
import LwTourCard from './lw/LwTourCard.vue';
import { createEngine, STATUS } from '../tours/engine';
import { audienceOf, loadTour, TOUR_IDS } from '../tours/registry';
import { readStory, writeStory, clearStory, rememberContext, markDone } from '../tours/persistence';
import {
  campaignFrom,
  track as trackUmami,
  trackWalkthrough as trackWalkthroughUmami,
} from '../tours/analytics';
import {
  cardPosition,
  dockedScrollDelta,
  freeRegion,
  isNarrow,
  pickCorner,
  revealDelta,
  targetSelector,
  clipRect,
} from '../tours/geometry';
import { createRequestLog, pathMatcher, waitForElement } from '../tours/waitFor';
import { createActionRun } from '../tours/actionRun';
import { controlIn, fieldIn, namedClickable } from '../tours/autoplay';
import { fill } from '../tours/context';
import { prepareContext } from '../tours/prepareContext';
import { readCompletions } from '../lib/checklistTrail';
import * as spotlight from '../tours/spotlight';
import { fetchPriceLine } from '../lib/pricing';
import { grade } from '../training/grade';
import { kindOf, moduleOf } from '../training/kinds';
import { courseBar } from '../training/courses';
import { readRun, record, refuse, writeRun } from '../training/stepLog';
import {
  renderer,
  emit as emitRender,
  box as filmBox,
  actionKind,
  entityOf,
  afterPaint,
} from '../training/render';

const DEFAULT_WAIT_MS = 4000;
// A walkthrough's targets are often a row the step itself just caused (the
// load it created, the invoice it saved), behind a list that refetches.
// Pointing at it is the whole step, so it gets longer before the centred
// fallback (found by the Playwright run on a slow stack).
const TASK_WAIT_MS = 10000;
const SLUG = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

// Spec 33 section 5.2: the pace the fallback types at. Zero under reduced motion.
const CADENCE_MS = 45;
const GAP_MS = 350;
// How long the ring shows a step as done before the next one opens, so the
// visitor sees their own action land before the ring moves on.
const DONE_BEAT_MS = 600;
// Mark, 2026-10-12: the walkthrough waits for the visitor to do each step.
// A visitor who has not managed it after this long gets a quiet way through
// ("Do this step for me"), and only then: offered from the start, it is the
// "Show me" button by another name.
const STALL_MS = 25000;
// Spec 53 F3: how long "Do this step for me" waits on the practice server
// hearing of it before the fallback acts anyway.
const ASSIST_WAIT_MS = 5000;
// While a task walkthrough runs, the ring follows its control as the page
// settles around it (a list that fills, a panel that loads above it).
const RING_FOLLOW_MS = 250;
// The walkthrough card's distance from the screen's edges: --lw-space-5,
// which LwTourCard's corner classes use.
const CORNER_INSET = 20;
// How often a step whose target is missing asks the page to reveal it.
const REVEAL_ASK_MS = 1000;

// What the card says while an action step is in each phase.
const PHASE_LINES = {
  playing: 'Doing it for you...',
  timeout: 'This is taking longer than it should. You can try again, or skip this step.',
};
// Spec 53 5.5, the practice fleet's simulated driver (a step's `drive`): how
// often the card asks whether the delivery is in, and how long past the
// server's own estimate it waits before saying something is wrong.
const DRIVE_POLL_MS = 5000;
const DRIVE_GRACE_MS = 60000;
const DRIVE_LINES = {
  sending: 'Sending the driver...',
  delivered: 'Delivered: the driver has completed the drop.',
  failed: 'The driver could not be sent. You can send them again.',
  slow: 'This is taking longer than it should. You can send the driver again.',
};
/** "On the way: about a minute and a half." from the server's estimate. */
function drivingLine(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return 'The driver is on the way.';
  const minutes = Math.round(s / 30) / 2;
  const about =
    minutes < 1
      ? 'under a minute'
      : minutes === 1
        ? 'about a minute'
        : minutes === 1.5
          ? 'about a minute and a half'
          : `about ${Math.round(minutes)} minutes`;
  return `The driver is on the way: ${about} to the drop and the dock.`;
}
const MISSING_LINE =
  'Something on this page is different from what the walkthrough expected. You can finish this step yourself or skip it.';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export default {
  name: 'TourHost',
  components: { LwTourCard },
  data() {
    return {
      // Filming a training video (training/render.js): set once, the page is
      // filmed from load to end.
      filming: Boolean(renderer()),
      engine: null,
      view: { status: STATUS.IDLE, index: 0, total: 0, step: null, isFirst: true, isLast: false },
      targetEl: null,
      position: { top: 0, left: 0 },
      docked: false,
      announcement: '',
      campaign: null,
      // Bumped on every step change, so a slow wait for an old step's target
      // cannot paint over the step the visitor has since moved to.
      token: 0,
      // The route the engine itself asked for, so its own navigation is not
      // mistaken for the visitor wandering off.
      expectedPath: null,
      loadIdCache: {},
      returnFocus: null,
      // The list price for the closing card, asked for when a tour starts
      // (lib/pricing.js). '' until it answers, and for good if it never does.
      priceLine: '',
      // Spec 33. 'story' or 'task' for the running tour.
      kind: 'story',
      // What a task walkthrough knows about this visitor's fleet ({po},
      // {customer}, ...), filled into each step before it runs.
      context: {},
      // The current action step's phase (actionRun.js), or null.
      phase: null,
      statusLine: '',
      // The element the ring is drawn around, and where. ringKey changes with
      // the element, so a new control gets the pulse afresh.
      ringEl: null,
      ringRect: null,
      ringKey: 0,
      reducedMotion: false,
      startedAt: 0,
      // A task walkthrough counts its card GROUPS, not its steps: a form's
      // fields are a step each (the ring moves field by field) under one
      // card that says "step 3 of 8" for all of them.
      groupNumber: 1,
      groupTotal: 1,
      // "Great. Now the equipment and the reference.": said on a step the
      // visitor reached by finishing the one before, and only then.
      ackLine: '',
      // The current action step has waited long enough on the visitor to
      // offer "Do this step for me".
      stalled: false,
      // A walkthrough card's corner (geometry.js pickCorner), and how far
      // down a top corner starts: below the app's header.
      cardCorner: 'bottom-right',
      cardTop: 0,
      // Spec 53: the running definition's `training` block, when it runs as
      // training (on a practice fleet), and what the person did at each step.
      trainingBlock: null,
      stepLog: [],
      // A `drive` step's simulated driver (spec 53 5.5): null (not sent),
      // 'sending', 'driving', 'delivered' or 'failed'.
      driveState: null,
    };
  },
  computed: {
    step() {
      return this.view.step;
    },
    /** The current step with this visitor's context filled in. */
    shown() {
      if (!this.step) return null;
      return this.kind === 'task' ? fill(this.step, this.context) : this.step;
    },
    running() {
      return this.view.status === STATUS.RUNNING;
    },
    paused() {
      return this.view.status === STATUS.PAUSED;
    },
    active() {
      return this.running || this.paused;
    },
    isAction() {
      return Boolean(this.step && Array.isArray(this.step.act));
    },
    hasChoices() {
      return Boolean(this.step && Array.isArray(this.step.choices) && this.step.choices.length);
    },
    isDemo() {
      const getters = (this.$store && this.$store.getters) || {};
      return Boolean(getters.isDemo);
    },
    /**
     * Spec 53 4.1: a practice fleet. A demo lease that carries a training
     * enrollment, and only that: a real tenant has no `demo` at all.
     */
    training() {
      const getters = (this.$store && this.$store.getters) || {};
      return this.isDemo && Boolean(getters.trainingLease);
    },
    /** The running walkthrough is a training module: its steps are logged and graded. */
    inTraining() {
      return this.training && Boolean(this.trainingBlock);
    },
    /**
     * Spec 53 5.5: this run may send the simulated driver. A practice fleet's
     * training run only, with the load the walkthrough follows: never an
     * anonymous visitor's demo lease, and a real tenant runs no walkthrough
     * at all.
     */
    mayDrive() {
      return this.inTraining && Boolean(this.context && this.context.loadId);
    },
    /** A `drive` step keeps Next back until the delivery is in (or the drive failed). */
    driveHolds() {
      if (!this.step || !this.step.drive || !this.mayDrive) return false;
      return this.driveState !== 'delivered' && this.driveState !== 'failed';
    },
    /** "Dispatch a load · 3 of 7 · Assign it", for a course's chapter. */
    course() {
      return this.inTraining && this.definition ? courseBar(this.definition) : null;
    },
    ctaActions() {
      const tag = this.campaign ? `?utm_campaign=${encodeURIComponent(this.campaign)}` : '';
      return [
        {
          id: 'signup',
          label: 'Start my own fleet',
          primary: true,
          href: `https://app.ladeway.com/static/signup.html${tag}`,
        },
        { id: 'ask', label: 'Ask a question first', href: `https://ladeway.com/${tag}#start` },
      ];
    },
    /** Links on the card: the story's closing pair, or a branch point's outward choices. */
    cardActions() {
      // Spec 53 4.7: no sign-up prompts in a practice fleet.
      if (!this.step || this.training) return [];
      if (this.step.cta) return this.ctaActions;
      if (!this.hasChoices) return [];
      const outward = { signup: this.ctaActions[0], ask: this.ctaActions[1] };
      return this.step.choices
        .filter(choice => outward[choice.goto])
        .map(choice => ({ ...outward[choice.goto], primary: false }));
    },
    /** The step's own buttons for the phase it is in (spec 33 section 5.8). */
    controls() {
      if (!this.step) return [];
      // Spec 33 7.3: the story's closing card offers the walkthroughs too.
      if (this.step.cta) return [{ id: 'try', label: 'Try it yourself' }];
      if (this.hasChoices) {
        return this.step.choices
          .map((choice, i) => ({ choice, i }))
          .filter(({ choice }) => !['signup', 'ask'].includes(choice.goto))
          .map(({ choice, i }) => ({ id: `choice-${i}`, label: choice.label }));
      }
      // Spec 53 5.5: the simulated driver, sent from the card. Filming has no
      // card: the host sends them itself (openDrive).
      if (this.step.drive) {
        if (!this.mayDrive || renderer()) return [];
        if (['sending', 'driving', 'delivered'].includes(this.driveState)) return [];
        const again = this.driveState === 'failed';
        return [{ id: 'drive', label: again ? 'Send the driver again' : 'Send the driver' }];
      }
      if (!this.isAction) return [];
      switch (this.phase) {
        // The visitor does the step. Only once they have been at it a while
        // does the card offer to do it for them, as a quiet link.
        case 'self':
          // Filming (training/render.js): the driver plays the step itself.
          if (renderer()) return [];
          return this.stalled && this.step.act.length
            ? [{ id: 'play', label: 'Do this step for me', variant: 'link' }]
            : [];
        case 'playing':
          return [{ id: 'stop', label: 'Stop' }];
        case 'refused':
        case 'timeout':
          return [{ id: 'retry', label: 'Try again' }];
        default:
          return [];
      }
    },
    /** What to do now, in a sentence: the step's `say`, while it is the visitor's to do. */
    instruction() {
      if (!this.isAction || !this.shown) return '';
      if (!['self', 'playing', 'waiting', 'done'].includes(this.phase)) return '';
      return this.shown.say || '';
    },
    /** The same, in a few words, beside the ringed control. */
    tagText() {
      if (!this.isAction || !this.shown || this.phase !== 'self') return '';
      return this.shown.tag || '';
    },
    // The tag sits above the ring's right end, or under it when the control
    // is too near the top of the screen for that.
    tagBelow() {
      return Boolean(this.ringRect && this.ringRect.top < 44);
    },
    tagStyle() {
      const r = this.ringRect;
      if (!r) return null;
      const right = Math.max(8, window.innerWidth - (r.left + r.width) - 4);
      return this.tagBelow
        ? { top: `${r.top + r.height + 8}px`, right: `${right}px` }
        : { top: `${r.top - 8}px`, right: `${right}px` };
    },
    ringStyle() {
      const r = this.ringRect;
      if (!r) return null;
      const pad = 4;
      return {
        top: `${r.top - pad}px`,
        left: `${r.left - pad}px`,
        width: `${r.width + pad * 2}px`,
        height: `${r.height + pad * 2}px`,
      };
    },
  },
  watch: {
    // GET /organization lands after mount; a tour link must not be lost to that.
    isDemo(now) {
      if (now) this.maybeStart();
    },
    // The same race for the practice fleet: a lease can say it is one after
    // a tour was asked for. A tour it may not run stops; one it may, starts.
    training(now) {
      if (this.engine && this.active && this.definition && !this.mayStart(this.definition)) {
        this.endReasonNote = 'switch';
        this.engine.end('interrupted');
      }
      if (now) this.maybeStart();
    },
    '$route.path'(path) {
      this.onRouteChange(path);
    },
    // Spec 53 15: Practice starts the task and its checklist (`?task=`,
    // TaskHost). Cards still running in this tab end: one run at a time.
    '$route.query.task'(task) {
      if (!task || !this.engine || !this.active) return;
      this.endReasonNote = 'switch';
      this.engine.end('exited');
      clearStory();
    },
    // Spec 33 section 5.10: the walkthrough menu (and a branch point's "next
    // walkthrough") start a tour by putting ?tour= on the current address.
    async '$route.query.tour'(tour) {
      if (!tour || !this.isDemo) return;
      if (this.engine && this.active && this.engine.definitionId === tour) return;
      // Only a tour this tenant may run ends the one that is running.
      const known = typeof tour === 'string' && SLUG.test(tour) && TOUR_IDS.includes(tour);
      const definition = known ? await loadTour(tour) : null;
      if (!this.mayStart(definition)) return;
      if (this.engine && this.active) {
        if (this.engine.definitionId === tour) return;
        this.endReasonNote = 'switch';
        this.engine.end('exited');
      }
      this.maybeStart(this.pendingEntry || 'menu');
      this.pendingEntry = null;
    },
  },
  created() {
    // Not reactive on purpose: a live run, an HTTP interceptor, timers.
    this.actionRun = null;
    this.requests = null;
    this.stepMode = 'read';
    this.pendingEntry = null;
    this.endReasonNote = null;
    this.starting = false;
    this.doneTimer = null;
    // Filming (training/render.js): a done step waiting for the driver, and
    // the last stage (ring and card) reported to it.
    this.renderAdvance = null;
    this.stageKey = null;
    this.watchAbort = null;
    this.stallTimer = null;
    this.followTimer = null;
    // The placeholder a step put in a control, and what was there before.
    this.hintRestore = null;
    // What the ring is on, as the step named it, so a control Vue re-renders
    // is found again rather than lost.
    this.ringSource = null;
    // The step being left was finished by the visitor (not skipped, not
    // backed out of), so the next one may acknowledge it.
    this.arrivedByDone = false;
    // A task step whose target is not on the page: { step, token }. Looked
    // for again as the page changes (followRing), never given up on.
    this.missing = null;
    // The definition running now, and its registry entry when it is a
    // training module (training/kinds.js moduleOf).
    this.definition = null;
    this.trainingModule = null;
    this.trainingStartedAt = null;
    // What this training run's steps captured from the server (spec 33
    // `capture`: { loadId, driverId, ... }), handed on with the log so the
    // practice server can check the evidence is the trainee's own (4.5).
    this.trainingCaptured = {};
    // Spec 53 5.5: the next look at the simulated driver's delivery, Next
    // held for the film until it is in, and the load already delivered (so
    // Back onto the step does not ask for a second drive).
    this.driveTimer = null;
    this.driveAdvance = null;
    this.deliveredLoad = null;
  },
  mounted() {
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('resize', this.place);
    window.addEventListener('scroll', this.place, true);
    document.addEventListener('pointerdown', this.onPageInput, true);
    document.addEventListener('keydown', this.onPageInput, true);
    window.addEventListener('pagehide', this.onPageHide);
    const filming = renderer();
    if (filming) {
      filming.host = {
        start: id =>
          this.$router.replace({
            path: this.$route.path,
            query: { ...this.$route.query, tour: id },
          }),
        play: () => this.onControl('play'),
        next: () => this.filmNext(),
        release: () => this.releaseRender(),
        // Where the module starts, so its intro plays over that page.
        firstPath: async id => {
          const definition = await loadTour(id);
          const steps = definition ? definition.chapters.flatMap(c => c.steps || []) : [];
          const first = steps.find(s => s.route);
          const location = first ? await this.resolveRoute(first.route) : null;
          return location ? this.$router.resolve(location).fullPath : null;
        },
      };
    }
    this.maybeStart();
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.place);
    window.removeEventListener('scroll', this.place, true);
    document.removeEventListener('pointerdown', this.onPageInput, true);
    document.removeEventListener('keydown', this.onPageInput, true);
    window.removeEventListener('pagehide', this.onPageHide);
    this.stopAction();
    this.clearStage();
    clearInterval(this.followTimer);
    if (this.requests) this.requests.uninstall();
    spotlight.destroy();
  },
  methods: {
    /**
     * Who may start what (spec 53 section 4.1). A real tenant: nothing, ever.
     * A practice fleet: only a task walkthrough written for training (never
     * the sales story). An anonymous demo visitor: what is written for the
     * demo, as before.
     */
    mayStart(definition) {
      if (!definition || !this.isDemo) return false;
      const audience = audienceOf(definition);
      if (this.training) return definition.kind === 'task' && audience.includes('training');
      return audience.includes('demo');
    },

    /** Umami, never in a practice fleet (spec 53 4.7: no `demo-*` events there). */
    track(name, data) {
      if (this.training) return false;
      return trackUmami(name, data);
    },
    trackWalkthrough(event, data) {
      if (this.training) return false;
      return trackWalkthroughUmami(event, data);
    },

    /**
     * A tour starts from `?tour=<id>` (optionally `&chapter=`), or picks up
     * where this tab left off. Only on the demo, and one at a time.
     */
    async maybeStart(entry = 'link') {
      if ((this.engine && this.active) || !this.isDemo || this.starting) return;
      const query = (this.$route && this.$route.query) || {};
      const saved = readStory();
      const asked =
        typeof query.tour === 'string' && SLUG.test(query.tour) && TOUR_IDS.includes(query.tour)
          ? query.tour
          : null;
      // Spec 53 15: a practice run going as the task and its checklist
      // (TaskHost) is not picked up here; only a link (`?tour=`, the
      // checklist's own hand-over among them) starts the cards over it.
      const free = readRun();
      const freeRunning =
        free && free.mode === 'free' && !free.finished && !free.handover && !free.left;
      // Practice just asked for the checklist (`?task=`): saved cards wait.
      const taskAsked = typeof query.task === 'string' && query.task !== '';
      const id = asked || (freeRunning || taskAsked ? null : saved && saved.id);
      if (!id) return;

      this.starting = true;
      try {
        const definition = await loadTour(id);
        if (!definition || (this.engine && this.active)) return;
        if (!this.mayStart(definition)) return;
        this.definition = definition;

        this.campaign = campaignFrom(window.location.search) || (saved && saved.campaign) || null;
        // The closing card's price is the sales story's; a practice fleet sells nothing.
        if (!this.training) {
          fetchPriceLine().then(line => {
            this.priceLine = line;
          });
        }
        this.returnFocus = document.activeElement;
        const user = (this.$store && this.$store.getters && this.$store.getters.user) || null;
        const chapter =
          typeof query.chapter === 'string' && SLUG.test(query.chapter) ? query.chapter : null;
        // A fresh link wins over a remembered position; a refresh resumes.
        const resuming = !asked || (saved && saved.id === asked && !chapter);
        const resumeAt = resuming && saved && saved.id === id ? saved.index : null;

        this.kind = definition.kind === 'task' ? 'task' : 'story';
        let context = null;
        if (this.kind === 'task') {
          context = await this.prepareContext(definition, Number.isInteger(resumeAt));
          // Spec 53 15.1: handed over from the checklist, the cards suggest
          // what it did (the yard is found from live tracking, which moves).
          const run = readRun();
          if (run && run.handover && run.tourId === definition.id && !run.finished) {
            context = { ...context, ...run.context };
          }
          this.requests = this.requests || createRequestLog(this.$http, this.filmRequests());
          this.requests.install();
        }
        this.context = context || {};

        const engine = createEngine({
          definition,
          role: user && user.role,
          context,
          onChange: state => this.onEngineChange(state),
        });
        engine.definitionId = definition.id;
        this.engine = engine;
        this.startedAt = Date.now();

        if (this.kind === 'task') {
          this.trackWalkthrough('start', {
            walkthrough: id,
            entry: Number.isInteger(resumeAt) ? 'resume' : entry,
            campaign: this.campaign || 'none',
          });
        } else {
          this.track('demo-tour-start', {
            tour: id,
            chapter: chapter || 'start',
            campaign: this.campaign || 'none',
          });
        }
        // The link has done its job. Left in the address, `?tour=` would start
        // the tour over on every refresh, including after the visitor left it;
        // from here on the tab's own memory (persistence.js) is what resumes it.
        if (asked && typeof this.$router.replace === 'function') {
          const rest = { ...query };
          delete rest.tour;
          delete rest.chapter;
          delete rest.step;
          Promise.resolve(this.$router.replace({ path: this.$route.path, query: rest })).catch(
            () => {}
          );
        }
        const restored = Number.isInteger(resumeAt) && saved;
        this.openTrainingRun(definition, Boolean(restored));
        // Spec 53 15.1: the checklist hands over at the first step not yet
        // done (`?step=`), not at the top of the chapter.
        const stepAsked =
          typeof query.step === 'string' && SLUG.test(query.step) ? query.step : null;
        const stepAt = stepAsked ? (engine.steps || []).findIndex(s => s.id === stepAsked) : -1;
        engine.start({
          chapter: stepAt >= 0 ? null : chapter,
          at: Number.isInteger(resumeAt) ? resumeAt : stepAt >= 0 ? stepAt : null,
          floor: restored ? saved.floor : 0,
          history: restored ? saved.history : [],
        });
        if (restored && saved.status === 'paused') engine.pause();
      } finally {
        this.starting = false;
      }
    },

    /** What a task walkthrough needs before step one (tours/prepareContext.js). */
    prepareContext(definition, resuming) {
      return prepareContext({
        http: this.$http,
        definition,
        resuming,
        lease: this.training ? this.$store.getters.trainingLease : null,
      });
    },

    onEngineChange(state) {
      const previous = this.view;
      const previousChapter = previous.step && previous.step.chapterId;
      const moved =
        state.status !== previous.status || state.index !== previous.index || !state.step;
      this.view = state;
      this.token += 1;
      if (moved) this.stopAction();
      const finished = this.arrivedByDone;
      this.arrivedByDone = false;

      if (state.status === STATUS.ENDED) {
        emitRender('end', { reason: state.endReason });
        clearStory();
        this.clearStage();
        clearInterval(this.followTimer);
        this.followTimer = null;
        this.reportEnd(state, previous);
        this.closeTrainingRun(state);
        if (this.requests) this.requests.uninstall();
        this.engine = null;
        this.definition = null;
        if (this.returnFocus && this.returnFocus.focus) this.returnFocus.focus();
        return;
      }

      writeStory({
        id: state.tourId,
        index: state.index,
        status: state.status,
        campaign: this.campaign,
        floor: state.floor,
        history: state.history,
      });
      if (state.status === STATUS.PAUSED) return this.clearStage();

      if (this.kind === 'task') {
        this.countGroups(state.index);
        const step = state.step;
        this.ackLine =
          finished && state.lastMove === 'next' && step.ack ? fill(step.ack, this.context) : '';
        if (!this.followTimer) {
          this.followTimer = setInterval(() => this.followRing(), RING_FOLLOW_MS);
        }
        if (state.lastMove !== 'resume' || moved) {
          this.trackWalkthrough('stepStart', {
            walkthrough: state.tourId,
            step: state.step.id,
            n: state.index + 1,
          });
        }
      } else if (state.step && state.step.chapterId !== previousChapter) {
        this.track('demo-tour-chapter', {
          chapter: state.step.chapterId,
          campaign: this.campaign || 'none',
        });
      }
      this.stage(state.step, this.token);
    },

    reportEnd(state, previous) {
      const lastStep = previous && previous.step ? previous.step.id : 'none';
      if (this.kind === 'task') {
        if (state.endReason === 'completed') {
          markDone(state.tourId);
          this.trackWalkthrough('complete', {
            walkthrough: state.tourId,
            seconds: Math.round((Date.now() - this.startedAt) / 1000),
          });
        } else {
          this.trackWalkthrough('abandon', {
            walkthrough: state.tourId,
            step: lastStep,
            reason: this.endReasonNote || (state.endReason === 'interrupted' ? 'left' : 'exit'),
          });
        }
        this.endReasonNote = null;
        return;
      }
      this.track(state.endReason === 'completed' ? 'demo-tour-complete' : 'demo-tour-exit', {
        tour: state.tourId,
        step: state.index + 1,
        campaign: this.campaign || 'none',
      });
    },

    // ----------------------------------------------------------------------
    // Training (spec 53 sections 4.3 and 4.4)
    // ----------------------------------------------------------------------

    /**
     * A training module's run begins: a fresh step log, or, when the tab is
     * picking the same module up again (a refresh), the log it had.
     */
    openTrainingRun(definition, resuming) {
      this.trainingBlock = null;
      this.trainingModule = null;
      this.stepLog = [];
      this.trainingCaptured = {};
      if (!this.training || !definition.training) return;
      this.trainingBlock = definition.training;
      this.trainingModule = moduleOf(definition);
      const saved = readRun();
      const same = saved && saved.tourId === definition.id && !saved.finished;
      // Spec 53 15.1: "Guide me step by step" from the checklist (TaskHost)
      // carries what the person already did into the cards.
      const handedOver = same && saved.handover === true;
      if ((resuming || handedOver) && same && saved.version === definition.version) {
        this.stepLog = saved.steps;
        this.trainingStartedAt = saved.startedAt;
        this.trainingCaptured = saved.captured;
      } else {
        this.trainingStartedAt = new Date().toISOString();
      }
      this.saveTrainingRun(false);
    },

    saveTrainingRun(finished) {
      if (!this.trainingModule) return;
      writeRun({
        tourId: this.definition ? this.definition.id : this.trainingModule.id,
        module: this.trainingModule.id,
        version: this.trainingModule.version,
        startedAt: this.trainingStartedAt,
        steps: this.stepLog,
        captured: this.trainingCaptured,
        finished,
      });
    },

    /** What the person did at a step: 'self' | 'shown' | 'read' | 'skipped'. */
    logStep(step, mode) {
      if (!this.inTraining || !step) return;
      this.stepLog = record(this.stepLog, { id: step.id, mode, title: step.field || step.title });
      this.saveTrainingRun(false);
    },

    /**
     * Spec 53 4.3: an Enter step met again after Back, that the person has
     * not yet done themselves (the fallback filled it, or they skipped it),
     * waits for their own edit: what the fallback left in the box must not
     * finish the step the moment it opens.
     */
    redoable(step) {
      if (!this.inTraining || kindOf(step) !== 'enter') return step;
      const logged = this.stepLog.find(entry => entry.id === step.id);
      if (!logged || !logged.mode || logged.mode === 'self') return step;
      return { ...step, until: { ...step.until, redo: true } };
    },

    /** The server said no. Logged; the step stays open and nothing fails. */
    logRefusal(step) {
      if (!this.inTraining || !step) return;
      this.stepLog = refuse(this.stepLog, { id: step.id, title: step.field || step.title });
      this.saveTrainingRun(false);
    },

    /**
     * The run is over. A finished module is handed on, with its log and the
     * page's own grade of it, to whatever posts it to the practice fleet's
     * server (which has the last word) and celebrates (spec 53 WP8):
     * `lw:training-end` on window. One that was left stays open (4.6) and is
     * announced the same way, `reason` saying which.
     */
    closeTrainingRun(state) {
      const module = this.trainingModule;
      if (!module) return;
      // A filmed run is nobody's attempt: no grade, no celebration.
      if (renderer()) {
        this.trainingBlock = null;
        this.trainingModule = null;
        return;
      }
      const completed = state.endReason === 'completed';
      this.saveTrainingRun(completed);
      const steps = this.stepLog.map(entry => ({ ...entry }));
      window.dispatchEvent(
        new window.CustomEvent('lw:training-end', {
          detail: {
            reason: state.endReason,
            tourId: state.tourId,
            module: module.id,
            version: module.version,
            startedAt: this.trainingStartedAt,
            steps,
            captured: { ...this.trainingCaptured },
            result: grade(module, steps),
          },
        })
      );
      this.trainingBlock = null;
      this.trainingModule = null;
    },

    /** Route first, then target, then the card (spec 25 section 5.1). Never a stall. */
    async stage(rawStep, token) {
      this.clearStage();
      const step = this.kind === 'task' ? fill(rawStep, this.context) : rawStep;
      this.announcement = `${step.chapterTitle}. Step ${this.view.index + 1} of ${this.view.total}: ${step.title}`;
      this.reducedMotion = prefersReducedMotion();
      this.stepMode = 'read';

      if (step.route) {
        const location = await this.resolveRoute(step.route);
        if (token !== this.token) return;
        if (location) {
          const resolved = this.$router.resolve(location);
          // A step may name a query on the page it is already on (the load
          // page's ?tab=pod): navigate when a key it names differs, but never
          // strip a query a step does not mention.
          const wanted = location.query || {};
          const current = this.$route.query || {};
          const queryDiffers = Object.keys(wanted).some(
            k => String(wanted[k]) !== String(current[k])
          );
          if (resolved.path !== this.$route.path || queryDiffers) {
            this.expectedPath = resolved.path;
            await this.$router.push(location).catch(() => {});
            if (token !== this.token) return;
          }
        }
      }

      // An action step arms its outcome before anything else can happen on
      // the page, so a visitor quicker than the card is never missed.
      if (Array.isArray(step.act)) this.armAction(step, token);

      // A walkthrough's target may be in a part of the page that is not
      // showing yet: the visitor's new load on page 2 of the live map's list
      // (found on the live demo, 2026-10-22). Ask the page to show it, and
      // once the wait below gives up, keep looking (followRing): the visitor
      // may page to it themselves, and the ring should then appear.
      if (step.target && this.kind === 'task') {
        this.missing = { step, token, waiting: true, askedAt: Date.now() };
        this.requestReveal(step.target);
      }
      const el = step.target
        ? await this.waitForTarget(
            step.target,
            step.waitMs || (this.kind === 'task' ? TASK_WAIT_MS : DEFAULT_WAIT_MS),
            token
          )
        : null;
      if (token !== this.token) return;
      if (this.missing && this.missing.token === token) {
        if (el) this.missing = null;
        else this.missing.waiting = false;
      }

      if (this.kind === 'task') {
        await this.showTask(step, el, token);
        if (token !== this.token) return;
      } else if (step.spotlight === 'ring') {
        spotlight.hide();
        this.targetEl = el;
        this.setRing(el);
      } else {
        this.targetEl = el;
        this.docked = isNarrow(window.innerWidth);
        if (el && this.docked) {
          // A phone: the card takes the bottom of the screen, so the page is
          // scrolled to put the target in what is left above it, and only then
          // is the hole cut (the library measures when it is asked to).
          await this.$nextTick();
          this.reserveDockSpace();
          this.scrollAboveDockedCard(el);
          spotlight.show(el, { scroll: false });
          this.settleDocked(el, token);
        } else if (el) {
          spotlight.show(el);
        }
      }
      if (step.watch) this.watchFor(step, token);
      if (step.drive) this.openDrive(step, token);
      await this.$nextTick();
      this.place();
      if (token === this.token) {
        const action = Array.isArray(step.act);
        emitRender('step.start', {
          step: step.id,
          index: this.view.index,
          action,
          byHand: action && step.act.length === 0,
          last: Boolean(this.view.isLast),
        });
        // A step that asks for nothing is finished as soon as it is read.
        if (!action) emitRender('step.result', { step: step.id, ok: true, read: true });
      }
      // An action step of a walkthrough leaves focus where the visitor has
      // it: they are about to type into the page, and the card, mounted at
      // the end of the document, is the one place Tab cannot lead on from.
      const doing = this.kind === 'task' && Array.isArray(step.act);
      if (this.$refs.card && !this.pageDialogOpen() && !doing) this.$refs.card.focus();
    },

    /**
     * A walkthrough step on the page (Mark, 2026-10-12). The card keeps one
     * place, docked in the corner, so the visitor never has to find it
     * again. What moves is the ring: onto the exact control to use (the
     * customer box, the Type select, a stop's time, a driver's Select
     * button), never the panel around it. The page scrolls, smoothly, until
     * that control is wholly in view: below the app's header, and clear of
     * the card when the card sits in the same columns. An explanation step
     * keeps spec 25's spotlight on what it explains.
     */
    async showTask(step, el, token) {
      this.targetEl = el;
      this.docked = isNarrow(window.innerWidth);
      const focus = el ? this.focusOf(el, step) : null;
      const ringed = Array.isArray(step.act) || step.spotlight === 'ring';
      spotlight.hide();
      this.setRing(ringed ? focus : null, ringed ? step : null);
      if (ringed && Array.isArray(step.act) && focus) this.applyHint(focus, step.hint);
      await this.$nextTick();
      if (token !== this.token) return;
      this.reserveDockSpace();
      if (focus) {
        this.reveal(focus);
        this.settleReveal(focus, token);
      }
      if (!ringed && el) spotlight.show(el, { scroll: false });
    },

    /**
     * The control a step means, inside the element its target names: a
     * button by `name`, a field by its label (`field`), a row's action menu
     * by its trigger, a field wrapper by its input. Otherwise the target.
     */
    focusOf(el, step) {
      if (!el) return null;
      if (step.name) return namedClickable(el, step.name) || el;
      if (step.field) return fieldIn(el, step.field) || el;
      const first = Array.isArray(step.act) ? step.act[0] : null;
      if (first && first.op === 'menu') return el.querySelector('[aria-haspopup="menu"]') || el;
      if (el.classList && el.classList.contains('lw-c-field')) return controlIn(el) || el;
      return el;
    },

    /**
     * Put what to type in the box itself, greyed: the customer's name, the
     * PO, the place. Only in an empty text box (a select has no
     * placeholder, and a box with something in it already says something),
     * and taken out again when the step ends.
     */
    applyHint(control, text) {
      this.clearHint();
      if (this.filming) return;
      if (!text || !control || !/^(INPUT|TEXTAREA)$/.test(control.tagName)) return;
      if (control.tagName === 'INPUT' && !/^(text|search|tel|email|number|)$/.test(control.type)) {
        return;
      }
      this.hintRestore = {
        el: control,
        had: control.hasAttribute('placeholder'),
        value: control.getAttribute('placeholder'),
      };
      control.setAttribute('placeholder', text);
    },

    clearHint() {
      const saved = this.hintRestore;
      this.hintRestore = null;
      if (!saved || !saved.el) return;
      if (saved.had) saved.el.setAttribute('placeholder', saved.value);
      else saved.el.removeAttribute('placeholder');
    },

    /** The ancestor that scrolls `el` vertically, or null for the page itself. */
    scrollerOf(el) {
      let node = el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    },

    /** Scroll, smoothly unless motion is reduced, until `el` is wholly in the free area. */
    reveal(el, { smooth = true } = {}) {
      if (!el || !el.isConnected || typeof el.getBoundingClientRect !== 'function') return;
      const behavior = this.reducedMotion || !smooth ? 'auto' : 'smooth';
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      let target = el.getBoundingClientRect();
      // Sideways first, on its own: a cell at the far end of a wide table
      // scrolls inside the table's box, never the page.
      if ((target.right > viewport.width || target.left < 0) && el.scrollIntoView) {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        target = el.getBoundingClientRect();
      }
      const scroller = this.scrollerOf(el);
      const cardEl = this.$refs.card && this.$refs.card.$el;
      const card =
        cardEl && cardEl.getBoundingClientRect && (this.targetEl || this.ringEl)
          ? cardEl.getBoundingClientRect()
          : null;
      const region = freeRegion({
        scroller: scroller ? scroller.getBoundingClientRect() : null,
        viewport,
        card,
        target,
      });
      const delta = revealDelta({ target, region });
      if (!delta) return;
      const box = scroller || window;
      const at = () => (scroller ? scroller.scrollTop : window.scrollY);
      const before = at();
      if (typeof box.scrollBy === 'function') box.scrollBy({ top: delta, behavior });
      else if (scroller) scroller.scrollTop += delta;
      // A browser that does not animate the scroll (a background tab, some
      // embedded views) leaves the page where it was: then go there at once,
      // because a control the visitor cannot see is worse than a jump.
      if (behavior === 'smooth') {
        setTimeout(() => {
          if (at() !== before || !el.isConnected) return;
          if (typeof box.scrollBy === 'function') box.scrollBy({ top: delta, behavior: 'auto' });
        }, 700);
      }
    },

    /**
     * A page is still arriving when its step opens (a list fills, a panel
     * loads above the form), and what was revealed can be pushed back out of
     * view. So the reveal is repeated twice as the page settles, and given
     * up the moment the visitor scrolls for themselves: then it is their page.
     */
    settleReveal(el, token) {
      let theirs = false;
      const mine = () => {
        theirs = true;
      };
      window.addEventListener('wheel', mine, { once: true, passive: true });
      window.addEventListener('touchstart', mine, { once: true, passive: true });
      [450, 1300].forEach(ms =>
        setTimeout(() => {
          if (theirs || token !== this.token || !this.running) return;
          this.reveal(this.ringEl && this.ringEl.isConnected ? this.ringEl : el);
        }, ms)
      );
    },

    /** Keep the ring on its control while the page moves around it. */
    followRing() {
      if (!this.running || this.kind !== 'task') return;
      this.lookForMissing();
      this.placeCorner();
      const source = this.ringSource;
      if (this.ringEl && !this.ringEl.isConnected && source && source.target) {
        // Vue re-rendered the control: find the new one and carry on.
        const again = document.querySelector(targetSelector(source.target));
        const found = again ? this.focusOf(again, source) : null;
        if (found) {
          this.ringEl = found;
          if (Array.isArray(source.act)) this.applyHint(found, source.hint);
        }
      }
      if (this.ringEl) this.ringRect = this.rectOf(this.ringEl);
      if (this.targetEl && !this.ringEl) spotlight.refresh();
      this.reportStage();
    },

    /**
     * Filming (training/render.js): where the ringed control and the card
     * are, whenever either moves, so the mascot laid over the video can look
     * at what is being pressed. CSS pixels, centre and size.
     */
    reportStage() {
      if (!renderer()) return;
      const key = b => (b ? [b.x, b.y, b.w, b.h].map(Math.round).join(',') : 'none');
      const ring = filmBox(this.ringRect);
      const cardEl = this.$refs.card && this.$refs.card.$el;
      const card = filmBox(
        cardEl && cardEl.getBoundingClientRect ? cardEl.getBoundingClientRect() : null
      );
      const now = `${key(ring)}|${key(card)}`;
      if (now === this.stageKey) return;
      this.stageKey = now;
      emitRender('stage', { step: this.step ? this.step.id : null, ring, card });
    },

    /**
     * A step whose target was not on the page: once the first wait has given
     * up, look again on every tick, and when it turns up, ring it as if it
     * had been there all along. Until then, keep asking the page to show it.
     */
    lookForMissing() {
      const missing = this.missing;
      if (!missing) return;
      if (missing.token !== this.token) {
        this.missing = null;
        return;
      }
      if (!missing.waiting) {
        const found = document.querySelector(targetSelector(missing.step.target));
        if (found && this.rectOf(found)) {
          this.missing = null;
          this.showTask(missing.step, found, missing.token).then(() => {
            if (missing.token === this.token) this.place();
          });
          return;
        }
      }
      if (Date.now() - missing.askedAt >= REVEAL_ASK_MS) {
        missing.askedAt = Date.now();
        this.requestReveal(missing.step.target);
      }
    },

    /**
     * Ask whatever shows `target` to bring it into the page: a paged list
     * turns to the page it is on (LoadRail listens). `surface.thing:<key>`
     * arrives as { name: 'surface.thing', key }.
     */
    requestReveal(target) {
      const at = target.indexOf(':');
      const detail = {
        name: at === -1 ? target : target.slice(0, at),
        key: at === -1 ? null : target.slice(at + 1),
      };
      window.dispatchEvent(new window.CustomEvent('lw:tour-reveal', { detail }));
    },

    /**
     * The walkthrough card's corner: kept while it covers nothing the visitor
     * needs, moved to another corner when it would cover the ringed control,
     * an open menu's items or an open dialog's controls (geometry.js).
     */
    placeCorner() {
      if (this.kind !== 'task' || this.docked || !this.$refs.card) return;
      const card = this.$refs.card.size();
      if (!card.width || !card.height) return;
      const content = document.getElementById('content');
      const top = content ? Math.max(0, Math.round(content.getBoundingClientRect().top)) : 0;
      this.cardTop = top;
      const corner = pickCorner({
        viewport: { width: window.innerWidth, height: window.innerHeight },
        card,
        avoid: this.avoidRects(),
        current: this.cardCorner,
        inset: CORNER_INSET,
        top,
      });
      if (corner !== this.cardCorner) this.cardCorner = corner;
    },

    /** What the card must not cover, as viewport rects. */
    avoidRects() {
      const rects = this.ringRect ? [this.ringRect] : [];
      // Filming: the corner the mascot is composited into is not the card's.
      const reserved = renderer() && renderer().reserve;
      if (reserved) {
        const { left, top, width, height } = reserved;
        rects.push({ left, top, width, height, right: left + width, bottom: top + height });
      }
      const cardEl = this.$refs.card && this.$refs.card.$el;
      const add = el => {
        if (cardEl && cardEl.contains && cardEl.contains(el)) return;
        const r = el.getBoundingClientRect();
        if (r.width || r.height) rects.push(r);
      };
      document
        .querySelectorAll('[role="menu"] [role="menuitem"], [role="listbox"] [role="option"]')
        .forEach(add);
      const dialog = document.querySelector('.lw-c-dialog-scrim [role="dialog"]');
      if (dialog) {
        dialog.querySelectorAll('button, input, select, textarea, [role="combobox"]').forEach(add);
      }
      return rects;
    },

    /**
     * Which card of how many: consecutive steps with the same `group` (a
     * form's fields) are one card, so the count reads "3 of 8" while the
     * ring moves from field to field under it.
     */
    countGroups(index) {
      const steps = (this.engine && this.engine.steps) || [];
      let count = 0;
      let last = null;
      let at = 1;
      steps.forEach((s, i) => {
        const group = s.group || s.id;
        if (group !== last) {
          count += 1;
          last = group;
        }
        if (i === index) at = count;
      });
      this.groupNumber = at;
      this.groupTotal = Math.max(1, count);
    },

    /** An LwDialog traps Tab; taking focus from it would strand a keyboard user. */
    pageDialogOpen() {
      return Boolean(document.querySelector('.lw-c-dialog-scrim [role="dialog"]'));
    },

    // ----------------------------------------------------------------------
    // Action steps (spec 33 section 5.4)
    // ----------------------------------------------------------------------

    armAction(step, token) {
      const reduced = this.reducedMotion;
      const run = createActionRun({
        step: this.redoable(step),
        requests: this.requests || { waitFor: () => Promise.resolve(null) },
        selector: targetSelector,
        find: target => waitForElement(targetSelector(target), { timeoutMs: step.waitMs || 6000 }),
        cadenceMs: reduced ? 0 : CADENCE_MS,
        gapMs: reduced ? 0 : GAP_MS,
        onPhase: (phase, detail) => this.onPhase(phase, detail, token),
        onOp: (op, el) => this.onOp(op, el, token),
        location: () => this.$route,
      });
      this.actionRun = run;
      // Every action step starts as the visitor's to do.
      this.phase = 'self';
      this.stepMode = 'self';
      this.statusLine = '';
      this.stalled = false;
      clearTimeout(this.stallTimer);
      this.stallTimer = setTimeout(() => {
        if (token === this.token && this.phase === 'self') this.stalled = true;
      }, STALL_MS);
      run.result.then(result => this.onActionResult(step, result, token));
    },

    onPhase(phase, detail, token) {
      if (token !== this.token) return;
      this.phase = phase;
      // Back from the fallback to the person (Stop, Escape, a click on the
      // page, or a control the script could not find): whatever finishes the
      // step now, they do. Spec 53 4.3: a takeover is the person's step.
      if (phase === 'self' && detail) this.stepMode = 'self';
      if (phase === 'self' && detail && detail.reason && detail.reason !== 'takeover') {
        this.statusLine = MISSING_LINE;
      } else if (phase === 'self') {
        this.statusLine = '';
      } else if (phase === 'waiting') {
        this.statusLine =
          this.step && this.step.until && this.step.until.request ? 'Saving...' : '';
      } else if (PHASE_LINES[phase]) {
        this.statusLine = PHASE_LINES[phase];
      }
      if (this.statusLine) this.announcement = this.statusLine;
    },

    /** The fallback is acting on a control: ring that one, and bring it into view. */
    onOp(op, el, token) {
      if (token !== this.token) return;
      const ringed = controlIn(el) || el;
      emitRender('action', {
        step: this.step ? this.step.id : null,
        kind: actionKind(op.op),
        target: filmBox(ringed.getBoundingClientRect ? ringed.getBoundingClientRect() : null),
        label: op.field || op.name || op.target || null,
      });
      this.setRing(ringed, this.shown);
      if (op.say) this.announcement = op.say;
      this.reveal(ringed);
    },

    onActionResult(step, result, token) {
      if (token !== this.token || !this.engine) return;
      const tourId = this.engine.definitionId;
      if (result.outcome === 'done') {
        if (result.captured && Object.keys(result.captured).length) {
          this.context = { ...this.context, ...result.captured };
          rememberContext(result.captured);
          if (this.inTraining) {
            this.trainingCaptured = { ...this.trainingCaptured, ...result.captured };
          }
        }
        this.trackWalkthrough('stepComplete', {
          walkthrough: tourId,
          step: step.id,
          mode: this.stepMode,
        });
        this.logStep(step, this.stepMode);
        // The ring turns to "done" on the visitor's own control for a beat,
        // then the next step opens and says so ("Great. Now ...").
        this.phase = 'done';
        this.statusLine = '';
        this.announcement = 'Done.';
        const advance = () => {
          if (token !== this.token || !this.engine) return;
          this.arrivedByDone = true;
          this.engine.next({ commit: Boolean(step.commit) });
        };
        clearTimeout(this.doneTimer);
        // Filming: held until the driver has played the step's `why`.
        if (renderer()) {
          this.renderAdvance = advance;
          emitRender('step.done', { step: step.id });
          this.filmResult(step, result, token);
          return;
        }
        this.doneTimer = setTimeout(advance, this.reducedMotion ? 0 : DONE_BEAT_MS);
      } else if (result.outcome === 'refused') {
        const entry = result.entry || {};
        this.trackWalkthrough('stepRefused', {
          walkthrough: tourId,
          step: step.id,
          status: entry.status,
        });
        this.logRefusal(step);
        this.filmResult(step, result, token);
        this.phase = 'refused';
        this.statusLine = this.refusalText(step, entry);
        this.announcement = this.statusLine;
      } else if (result.outcome === 'timeout') {
        this.trackWalkthrough('stepTimeout', {
          walkthrough: tourId,
          step: step.id,
          waited: 'server',
        });
        emitRender('step.result', { step: step.id, ok: false, reason: 'timeout' });
        this.phase = 'timeout';
        this.statusLine = PHASE_LINES.timeout;
        this.announcement = this.statusLine;
      }
    },

    /** What to say when the server said no. Never the visitor's fault. */
    refusalText(step, entry) {
      if (step.refusal && step.refusal.body) return step.refusal.body;
      const detail = entry && entry.data && typeof entry.data.detail === 'string';
      if (detail) return entry.data.detail;
      if (entry && entry.status === 404) return 'That is not on the board any more.';
      if (entry && entry.status === 0) return 'The connection dropped. You can try again.';
      return 'That did not go through. You can try again, or skip this step.';
    },

    /**
     * Filming: the step's result, once the page shows it (training/render.js
     * `step.result`). The save's response is in; this waits for the render
     * that follows it and for the browser to paint that.
     */
    async filmResult(step, result, token) {
      if (!renderer()) return;
      await this.$nextTick();
      await afterPaint();
      if (token !== this.token) return;
      const entry = result.entry || {};
      const ok = result.outcome === 'done';
      const entity = ok ? entityOf(result.captured) : null;
      const said = entry.method ? `${entry.method} ${entry.path} ${entry.status}` : 'on the page';
      emitRender('step.result', {
        step: step.id,
        ok,
        ...(entry.status ? { status: entry.status } : {}),
        ...(entity ? { entity } : {}),
        summary: ok ? `${step.id} done (${said})` : `${step.id} refused (${said})`,
      });
    },

    /** Filming: every request and response the page makes, for the stream. Null otherwise. */
    filmRequests() {
      if (!renderer()) return null;
      let next = 0;
      const current = () => (this.step ? this.step.id : null);
      return {
        onRequest: ({ method, path }) => {
          next += 1;
          const id = `r${next}`;
          emitRender('request', { step: current(), id, method, path });
          return id;
        },
        onResponse: ({ id, status }) => emitRender('response', { step: current(), id, status }),
      };
    },

    /** Filming: the driver has finished a done step's `why`; move on. */
    releaseRender() {
      const advance = this.renderAdvance;
      this.renderAdvance = null;
      if (advance) advance();
    },

    stopAction() {
      clearTimeout(this.doneTimer);
      this.renderAdvance = null;
      clearTimeout(this.stallTimer);
      this.stalled = false;
      if (this.actionRun) this.actionRun.cancel();
      this.actionRun = null;
      if (this.watchAbort) this.watchAbort.abort();
      this.watchAbort = null;
      clearTimeout(this.driveTimer);
      this.driveTimer = null;
      this.driveAdvance = null;
      this.driveState = null;
      this.phase = null;
      this.statusLine = '';
    },

    onControl(id) {
      const run = this.actionRun;
      if (id.startsWith('choice-')) return this.choose(this.step.choices[Number(id.slice(7))]);
      if (id === 'try') return this.choose({ goto: 'tour:create-load' });
      if (id === 'drive') return this.sendDriver(this.token);
      if (id === 'retry') {
        this.stopAction();
        this.token += 1;
        return this.stage(this.view.step, this.token);
      }
      if (!run) return;
      if (id === 'play') {
        // "Do this step for me": recorded as shown, so the drop-off report
        // tells a step people finish apart from one they hand back.
        this.stepMode = 'shown';
        const told = this.tellAssist(this.step);
        if (!told) {
          run.play();
          return undefined;
        }
        const token = this.token;
        told.then(() => {
          // Still the same step, still theirs to do: not left (Back, Skip),
          // and not done by hand while the server was being told.
          if (token === this.token && this.actionRun === run && this.phase === 'self') run.play();
        });
      } else if (id === 'stop') {
        run.takeover();
      }
    },

    /**
     * Spec 53 F3: "Do this step for me" in a training run is told to the
     * practice server before the fallback acts, and the server grades that
     * step as at most 'shown' whatever the log it is later posted says
     * (api/app/Services/Training/assisted.js). Null when there is nobody to
     * tell: a demo or sales walkthrough, an anonymous visitor, or a filmed
     * run (training/render.js), which is nobody's attempt.
     *
     * A post that fails, or takes longer than ASSIST_WAIT_MS, still lets the
     * fallback act: leaving a trainee stuck on the step they asked help with
     * is worse than the server not knowing. Then the log is the only record
     * of that step, as it was before F3. An honest page still logs it as
     * 'shown', so what is lost is the check against a forged log, for that
     * step, when the practice server could not be reached; and that server
     * is the one the run's grade is posted to, so a run that could not tell
     * it usually can't be graded either until it is back.
     */
    tellAssist(step) {
      if (!this.inTraining || renderer() || !this.trainingModule || !step) return null;
      return Promise.resolve()
        .then(() =>
          this.$http.post(
            '/training/practice/assist',
            { module_id: this.trainingModule.id, step: step.id },
            { timeout: ASSIST_WAIT_MS }
          )
        )
        .catch(() => {});
    },

    /**
     * A TRUSTED click or key on the page while the fallback runs is the
     * visitor taking the wheel back. The fallback's own events are untrusted,
     * and anything on the card itself is the card's business.
     */
    onPageInput(event) {
      if (!event.isTrusted || this.phase !== 'playing' || !this.actionRun) return;
      const card = this.$refs.card && this.$refs.card.$el;
      if (card && card.contains && card.contains(event.target)) return;
      if (event.type === 'keydown' && /^(Shift|Control|Alt|Meta|Tab|Escape)$/.test(event.key)) {
        return;
      }
      this.stepMode = 'self';
      this.actionRun.takeover();
      this.trackWalkthrough('takeover', {
        walkthrough: this.engine && this.engine.definitionId,
        step: this.step && this.step.id,
      });
    },

    onPageHide() {
      if (this.kind !== 'task' || !this.engine || !this.active) return;
      this.trackWalkthrough('abandon', {
        walkthrough: this.engine.definitionId,
        step: this.step ? this.step.id : 'none',
        reason: 'left',
      });
    },

    /** A branch point's choice (spec 33 5.1, `choices`). */
    choose(choice) {
      if (!choice || !this.engine) return;
      const tourId = this.engine.definitionId;
      this.trackWalkthrough('branch', {
        walkthrough: tourId,
        step: this.step && this.step.id,
        choice: String(choice.goto),
      });
      const target = String(choice.goto || '');
      if (target.startsWith('tour:')) {
        const next = target.slice(5);
        this.engine.end('completed');
        this.pendingEntry = 'handoff';
        Promise.resolve(
          this.$router.replace({
            path: this.$route.path,
            query: { ...this.$route.query, tour: next },
          })
        ).catch(() => {});
        return;
      }
      if (target === 'explore' || target === 'end') {
        this.engine.end('completed');
        if (target === 'explore') {
          // The walkthrough menu (WP4) listens; nothing else needs to.
          window.dispatchEvent(new window.CustomEvent('lw:walkthroughs-open'));
        }
        return;
      }
      this.engine.goTo(target);
    },

    skip() {
      if (!this.engine) return;
      this.trackWalkthrough('stepSkip', {
        walkthrough: this.engine.definitionId,
        step: this.step.id,
      });
      this.logStep(this.shown, 'skipped');
      this.engine.skip();
    },

    // ----------------------------------------------------------------------
    // The simulated driver (spec 53 5.5, a step's `drive`)
    // ----------------------------------------------------------------------

    /**
     * A `drive` step opens: already delivered (Back onto it), it says so;
     * filmed, nobody can press the card's button, so the host sends the
     * driver itself; otherwise the card offers "Send the driver".
     */
    openDrive(step, token) {
      if (!this.mayDrive) return;
      if (
        this.deliveredLoad !== null &&
        String(this.deliveredLoad) === String(this.context.loadId)
      ) {
        this.driveState = 'delivered';
        this.statusLine = step.driveSays || DRIVE_LINES.delivered;
        return;
      }
      if (renderer()) this.sendDriver(token);
    },

    /**
     * "Send the driver": POST /demo/training/drive {load_id} on the practice
     * fleet (PracticeFleet.drive), once, then READ the load's proof of
     * delivery every few seconds until the drop has its completion. Reading,
     * not posting again: the drive under way lives in one API process, and
     * a second post landing on another would start a second drive. A drive
     * lost on the way (a restart) shows as slow, and the card offers to send
     * the driver again. Training runs only (mayDrive): a demo visitor's lease
     * and a real tenant never call it from here.
     */
    async sendDriver(token) {
      if (!this.mayDrive || ['sending', 'driving'].includes(this.driveState)) return;
      const step = this.step;
      const loadId = Number(this.context.loadId);
      const startedAt = Date.now();
      clearTimeout(this.driveTimer);
      this.driveState = 'sending';
      this.statusLine = DRIVE_LINES.sending;
      this.announcement = this.statusLine;
      let data;
      try {
        const res = await this.$http.post('/demo/training/drive', { load_id: loadId });
        data = (res && res.data) || {};
      } catch (err) {
        if (token !== this.token) return;
        const body = err && err.response && err.response.data;
        this.driveFailed(body && typeof body.detail === 'string' ? body.detail : null);
        return;
      }
      if (token !== this.token) return;
      // Already delivered; or a drive the server ran to the end before
      // answering (staging's pace) answers with its completion.
      if (data.delivered || (data.completion_id && !data.driving)) {
        this.driveDelivered(step, loadId);
        return;
      }
      this.driveState = 'driving';
      this.statusLine = drivingLine(data.seconds);
      this.announcement = this.statusLine;
      const limit = (Number(data.seconds) || 0) * 1000 * 2 + DRIVE_GRACE_MS;
      let drops = null;
      const look = async () => {
        if (token !== this.token) return;
        try {
          if (!drops) {
            const load = await this.$http.get(`/loads/${loadId}`);
            const stops = (load && load.data && load.data.stops) || [];
            drops = new Set(stops.filter(s => s && s.type === 'drop').map(s => String(s.id)));
          }
          const pod = await this.$http.get(`/loads/${loadId}/pod`);
          if (token !== this.token) return;
          const done = readCompletions(pod && pod.data).some(c => drops.has(String(c.stop_id)));
          if (done) {
            this.driveDelivered(step, loadId);
            return;
          }
        } catch (err) {
          // A read that failed is asked again next time, until the limit.
        }
        if (token !== this.token) return;
        if (Date.now() - startedAt > limit) {
          this.driveFailed(DRIVE_LINES.slow);
          return;
        }
        this.driveTimer = setTimeout(look, DRIVE_POLL_MS);
      };
      this.driveTimer = setTimeout(look, DRIVE_POLL_MS);
    },

    driveDelivered(step, loadId) {
      this.driveState = 'delivered';
      this.deliveredLoad = loadId;
      this.statusLine = (step && step.driveSays) || DRIVE_LINES.delivered;
      this.announcement = this.statusLine;
      const advance = this.driveAdvance;
      this.driveAdvance = null;
      if (advance) advance();
    },

    driveFailed(detail) {
      this.driveState = 'failed';
      this.statusLine = detail || DRIVE_LINES.failed;
      this.announcement = this.statusLine;
    },

    /**
     * Filming: the driver's Next. On a `drive` step it waits for the
     * delivery, so the film holds on the map until the truck has arrived
     * (the edit cuts the wait).
     */
    filmNext() {
      if (this.driveHolds) {
        this.driveAdvance = () => this.next();
        return;
      }
      this.next();
    },

    /** Keep an eye on something while the card is open (the arrival suggestion). */
    async watchFor(step, token) {
      const controller = new AbortController();
      this.watchAbort = controller;
      const el = await waitForElement(targetSelector(step.watch), {
        timeoutMs: 30 * 60000,
        signal: controller.signal,
      });
      if (!el || token !== this.token) return;
      this.statusLine = step.watchSays || 'There it is.';
      this.announcement = this.statusLine;
      this.setRing(el);
    },

    /** Ring `el` (the filled step `source` says how to find it again). */
    setRing(el, source = null) {
      if ((el || null) !== this.ringEl) this.ringKey += 1;
      this.ringEl = el || null;
      this.ringSource = el ? source : null;
      this.ringRect = el ? this.rectOf(el) : null;
      this.reportStage();
    },

    /** Where `el` is on screen, or null when it is not drawn (a hidden tab, a closed dialog). */
    rectOf(el) {
      if (!el || !el.isConnected) return null;
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height && !r.top && !r.left) return null;
      // Only what the person can see of it: the control's box, cut to every
      // ancestor that clips its overflow and to the viewport (geometry.js,
      // clipRect). A control scrolled wholly out of its dialog has no ring,
      // and gets it back when it scrolls into view again (place()).
      return clipRect(
        { top: r.top, left: r.left, width: r.width, height: r.height },
        this.clipsOf(el)
      );
    },

    /**
     * The visible boxes of every ancestor that clips its overflow (a dialog's
     * scrolling body, a panel with overflow hidden), then the viewport. Inner
     * edges: clientTop/clientLeft skip the border, clientWidth/clientHeight
     * skip the scrollbar.
     */
    clipsOf(el) {
      const clips = [];
      const clipping = /(auto|scroll|hidden|clip)/;
      let node = el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        if (clipping.test(style.overflowY) || clipping.test(style.overflowX)) {
          const r = node.getBoundingClientRect();
          clips.push({
            top: r.top + (node.clientTop || 0),
            left: r.left + (node.clientLeft || 0),
            width: node.clientWidth || r.width,
            height: node.clientHeight || r.height,
          });
        }
        node = node.parentElement;
      }
      clips.push({ top: 0, left: 0, width: window.innerWidth, height: window.innerHeight });
      return clips;
    },

    clearStage() {
      this.missing = null;
      this.targetEl = null;
      this.setRing(null);
      this.clearHint();
      spotlight.hide();
      document.documentElement.style.removeProperty('--lw-tour-dock-space');
    },

    /**
     * The card covers the bottom of the screen (docked on a phone, in the
     * corner on a desk), so the page needs that much more room to scroll or
     * its last element can never be brought up from behind the card.
     * app.scss adds this to #content's bottom padding.
     */
    reserveDockSpace() {
      const height = this.$refs.card ? this.$refs.card.size().height : 0;
      document.documentElement.style.setProperty(
        '--lw-tour-dock-space',
        // the card, plus the gap the reveal keeps above it, plus room for
        // the control itself to sit clear of both
        `${height ? height + 120 : 0}px`
      );
    },

    /**
     * A page is still arriving when its step opens: the map mounts, the lists
     * fill, and what was scrolled into place is pushed back out of it. So the
     * alignment is repeated a few times as the page settles, and abandoned the
     * moment the visitor scrolls for themselves, because then it is their page.
     */
    settleDocked(el, token) {
      let theirs = false;
      const mine = () => {
        theirs = true;
      };
      window.addEventListener('touchstart', mine, { once: true, passive: true });
      window.addEventListener('wheel', mine, { once: true, passive: true });
      [350, 1000, 2200].forEach(ms =>
        setTimeout(() => {
          if (theirs || token !== this.token || !this.running || !el.isConnected) return;
          this.scrollAboveDockedCard(el);
          spotlight.refresh();
        }, ms)
      );
    },

    /** The target now, or as soon as the page renders it; null when `waitMs` runs out. */
    async waitForTarget(target, waitMs, token) {
      const controller = new AbortController();
      const watch = setInterval(() => {
        if (token !== this.token) controller.abort();
      }, 100);
      const el = await waitForElement(targetSelector(target), {
        timeoutMs: waitMs,
        signal: controller.signal,
      });
      clearInterval(watch);
      if (!el && token === this.token && this.kind === 'task' && this.engine) {
        this.trackWalkthrough('stepTimeout', {
          walkthrough: this.engine.definitionId,
          step: this.step && this.step.id,
          waited: 'target',
        });
      }
      return el;
    },

    /**
     * A step's route with its lookups answered in THIS visitor's fleet (a
     * load's id differs in every tenant). Null when the lookup finds nothing:
     * the card then shows where the visitor already is, centred.
     */
    async resolveRoute(route) {
      const lookup = route.params && route.params.id && route.params.id.load;
      if (!lookup) return route;
      const id = await this.findLoadId(lookup);
      return id ? { ...route, params: { ...route.params, id: String(id) } } : null;
    },

    async findLoadId(lookup) {
      const key = typeof lookup === 'string' ? lookup : `po:${lookup.po}`;
      if (this.loadIdCache[key]) return this.loadIdCache[key];
      let id = null;
      try {
        if (lookup.po) {
          // The undelivered loads, as the board itself asks for them: a handful
          // of rows, and `po` is on every one. (`q` does not search the PO.)
          const res = await this.$http.get('/loads', { params: { delivered: false } });
          const hit = (res.data || []).find(l => l.po === lookup.po);
          id = hit ? hit.id : null;
        } else if (lookup === 'delivered-with-track') {
          const res = await this.$http.get('/loads', { params: { delivered: true } });
          const newest = (res.data || [])
            .slice()
            .sort((a, b) => b.id - a.id)
            .slice(0, 8);
          for (const load of newest) {
            // eslint-disable-next-line no-await-in-loop
            const trackRes = await this.$http.get(`/loads/${load.id}/track`).catch(() => null);
            const positions = trackRes && trackRes.data && trackRes.data.positions;
            if (Array.isArray(positions) && positions.length > 0) {
              id = load.id;
              break;
            }
          }
          if (!id && newest.length) id = newest[0].id;
        }
      } catch (e) {
        id = null;
      }
      if (id) this.loadIdCache[key] = id;
      return id;
    },

    scrollAboveDockedCard(el) {
      const cardHeight = this.$refs.card ? this.$refs.card.size().height : 0;
      // Horizontal first and on its own: a cell in a wide table scrolls into
      // view inside the table's own box, never the page.
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
      }
      const delta = dockedScrollDelta({
        target: el.getBoundingClientRect(),
        viewportHeight: window.innerHeight,
        cardHeight,
      });
      if (!delta) return;
      // Whichever ancestor actually scrolls vertically: #content on a desktop
      // layout, the document itself on a phone.
      let scroller = el.parentElement;
      while (scroller && scroller !== document.body) {
        const style = window.getComputedStyle(scroller);
        if (
          /(auto|scroll)/.test(style.overflowY) &&
          scroller.scrollHeight > scroller.clientHeight
        ) {
          break;
        }
        scroller = scroller.parentElement;
      }
      if (scroller && scroller !== document.body) scroller.scrollTop += delta;
      else window.scrollBy(0, delta);
    },

    place() {
      if (!this.running) return;
      if (this.ringEl) this.ringRect = this.ringEl.isConnected ? this.rectOf(this.ringEl) : null;
      this.docked = isNarrow(window.innerWidth);
      // A walkthrough's card does not move with the page (Mark, 2026-10-12:
      // "maintain position of the popover"): it is docked in the corner, and
      // the page scrolls the control clear of it instead (reveal). Only the
      // ring and the spotlight follow what they mark.
      if (this.kind === 'task') {
        if (this.targetEl && !this.ringEl) spotlight.refresh();
        this.placeCorner();
        return;
      }
      // The story's card sits beside its target.
      const anchor = this.ringEl || this.targetEl;
      if (this.docked || !anchor || !this.$refs.card) return;
      this.position = cardPosition({
        target: anchor.getBoundingClientRect(),
        card: this.$refs.card.size(),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        placement: (this.step && this.step.placement) || (this.ringEl ? 'right' : 'bottom'),
      });
    },

    /**
     * The visitor went somewhere the tour did not send them: step aside into
     * the pill. The login page and the subscription page end it silently. A
     * step whose own outcome navigates (Create load lands on the board) names
     * where it goes, and going there is not wandering off.
     */
    onRouteChange(path) {
      if (!this.running) return;
      if (this.expectedPath === path) {
        this.expectedPath = null;
        return;
      }
      if (/^\/(subscription|static)\b/.test(path)) return this.engine.end('interrupted');
      const navigates = this.shown && this.shown.navigates;
      if (navigates && this.isNavigation(navigates, path)) return;
      this.engine.pause();
    },

    /**
     * Whether `path` is where a step said its own outcome goes: a route
     * location, or a path pattern ('/documents/templates/:id/send-from-template')
     * for a destination whose id the walkthrough cannot know in advance.
     */
    isNavigation(navigates, path) {
      // A click that lands on a page that moves on by itself (the rail's
      // Team opens /team, which goes to its first tab) names every stop.
      if (Array.isArray(navigates)) return navigates.some(n => this.isNavigation(n, path));
      if (typeof navigates === 'string') return pathMatcher(navigates)(path);
      try {
        const resolved = this.$router.resolve(navigates);
        return Boolean(resolved && resolved.path === path);
      } catch (e) {
        return false;
      }
    },

    onKey(event) {
      if (!this.running || event.defaultPrevented) return;
      // Escape stops the fallback first (the step is the visitor's again);
      // a second Escape leaves.
      if (event.key === 'Escape' && this.phase === 'playing' && this.actionRun) {
        this.actionRun.takeover();
        return;
      }
      const tag = (event.target && event.target.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (event.target && event.target.isContentEditable))
        return;
      if (event.key === 'Escape') return this.exit();
      // Arrow keys move between explanation cards only: an action step moves
      // on when its outcome arrives, or by Skip.
      if (this.isAction || this.hasChoices) return;
      if (event.key === 'ArrowRight') return this.next();
      if (event.key === 'ArrowLeft') return this.back();
    },

    next() {
      if (!this.engine) return;
      if (!this.isAction && !this.hasChoices) this.logStep(this.shown, 'read');
      this.engine.next();
    },
    back() {
      if (this.engine) this.engine.back();
    },
    resume() {
      if (this.engine) this.engine.resume();
    },
    exit() {
      if (!this.engine) return;
      // A training module's last card is a Read step; Finish is its Next.
      if (this.view.isLast && !this.isAction && !this.hasChoices) this.logStep(this.shown, 'read');
      this.engine.end(this.view.isLast ? 'completed' : 'exited');
    },
    onAction(action) {
      this.track(action.id === 'signup' ? 'demo-cta-signup' : 'demo-cta-ask', {
        campaign: this.campaign || 'none',
      });
    },
  },
};
</script>

<style scoped>
.lw-p-tour__pill {
  appearance: none;
  background: var(--lw-navy-800);
  border: 0;
  border-radius: var(--lw-radius-pill);
  bottom: var(--lw-space-5);
  box-shadow: var(--lw-elev-3-shadow);
  color: var(--lw-fg-inverse);
  cursor: pointer;
  font: var(--lw-type-button);
  letter-spacing: var(--lw-type-button-tracking);
  padding: var(--lw-space-3) var(--lw-space-5);
  position: fixed;
  right: var(--lw-space-5);
  text-transform: var(--lw-type-button-transform);
  z-index: var(--lw-z-dialog);
}

.lw-p-tour__pill:hover,
.lw-p-tour__pill:focus-visible {
  background: var(--lw-navy-950);
}

/*
  Spec 33 P7/P8: where an action step wants the visitor, drawn around the one
  control to use instead of dimming the page. Never in the way: no pointer
  events, and it sits under the card. It pulses three times when it lands on
  a control (the element is keyed by the control, so each new one pulses
  afresh) and then stays as a steady outline: enough to draw the eye, not a
  light that blinks for as long as somebody is typing. The pulse is
  decoration and stops for reduced motion. When the visitor has done the
  step, it turns to the success colour for a beat before the next one opens.
*/
.lw-p-tour__ring {
  border: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  border-radius: var(--lw-radius-md);
  box-shadow: 0 0 0 0 var(--lw-focus-ring);
  pointer-events: none;
  position: fixed;
  z-index: calc(var(--lw-z-toast) * 10);
  animation: lw-tour-ring-pulse 0.9s var(--lw-motion-ease) 3;
}

.lw-p-tour__ring--done {
  border-color: var(--lw-success-fg);
  animation: none;
}

.lw-p-tour__ring--still {
  animation: none;
}

@keyframes lw-tour-ring-pulse {
  0% {
    box-shadow: 0 0 0 0 var(--lw-focus-ring);
  }

  60% {
    box-shadow: 0 0 0 var(--lw-space-3) var(--lw-focus-ring);
  }

  100% {
    box-shadow: 0 0 0 0 var(--lw-focus-ring);
  }
}

/*
  The step's few words, hung on the ring's top right corner like a label on
  a part: "Type Cascadia", "Choose Van". Read-only and click-through.
*/
.lw-p-tour__tag {
  background: var(--lw-focus-ring-color);
  border-radius: var(--lw-radius-sm);
  box-shadow: var(--lw-elev-2-shadow);
  color: var(--lw-fg-on-accent);
  font: var(--lw-type-caption-strong);
  margin: 0;
  max-width: 18rem;
  padding: var(--lw-space-1) var(--lw-space-2);
  pointer-events: none;
  position: fixed;
  transform: translateY(-100%);
  white-space: nowrap;
  z-index: calc(var(--lw-z-toast) * 10);
}

.lw-p-tour__tag--below {
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .lw-p-tour__ring {
    animation: none;
  }
}

/* Read by a screen reader, never seen. */
.lw-p-tour__live {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}
</style>
